import {
  AlarmClock,
  ArrowRight,
  BellRing,
  CalendarCheck,
  Check,
  CheckCircle2,
  Clock3,
  Download,
  LockKeyhole,
  Menu,
  Repeat2,
  ShieldCheck,
  Sparkles,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Brand } from './Brand'

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const faq = [
  ['¿TodoBack es realmente gratis?', 'Sí. Podés crear una cuenta, organizar tus tareas y usar recordatorios personales sin pagar. No necesitás tarjeta.'],
  ['¿En qué se diferencia de una lista de tareas común?', 'Una lista depende de que te acuerdes de abrirla. TodoBack prioriza lo importante y vuelve a avisarte cuando un pendiente sigue necesitando una decisión.'],
  ['¿Puedo usarlo como alarma para tareas?', 'Sí. Podés elegir fecha y hora, repetir el aviso y posponerlo. La aplicación también muestra recordatorios internos cuando el navegador bloquea los avisos del sistema.'],
  ['¿Funciona en celular y computadora?', 'Sí. TodoBack es responsive y se puede instalar como aplicación web en navegadores compatibles. Tus tareas se sincronizan con tu cuenta.'],
  ['¿Mis tareas son privadas?', 'Sí. La base de datos aplica controles por usuario para que cada cuenta solo pueda leer y modificar sus propios datos.'],
  ['¿Qué pasa si cierro la aplicación?', 'Con Web Push configurado, TodoBack puede enviar recordatorios aun cuando la aplicación esté cerrada. La entrega final también depende de los permisos y del ahorro de batería del dispositivo.'],
]

function usePublicMeta(title: string, description: string, robots = 'index,follow') {
  useEffect(() => {
    document.title = title
    const values = { description, robots }
    for (const [name, content] of Object.entries(values)) {
      let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
      if (!element) {
        element = document.createElement('meta')
        element.name = name
        document.head.appendChild(element)
      }
      element.content = content
    }
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = window.location.href.split(/[?#]/)[0]
  }, [description, robots, title])
}

export function LandingPage() {
  usePublicMeta(
    'TodoBack: app gratis de tareas y recordatorios persistentes',
    'TodoBack es la app gratis de tareas y recordatorios que vuelve a avisarte hasta que completes lo pendiente. Organizá tu día sin olvidar nada.',
  )
  const [mobileNav, setMobileNav] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null)

  useEffect(() => {
    const capture = (event: Event) => {
      event.preventDefault()
      setInstallPrompt(event as InstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', capture)
    return () => window.removeEventListener('beforeinstallprompt', capture)
  }, [])

  const install = async () => {
    if (!installPrompt) return
    await installPrompt.prompt()
    const choice = await installPrompt.userChoice
    if (choice.outcome === 'accepted') setInstallPrompt(null)
  }

  return <div className="marketing-page">
    <a className="skip-link" href="#contenido">Saltar al contenido</a>
    <header className="marketing-header">
      <a href="/" aria-label="TodoBack, inicio"><Brand /></a>
      <button className="marketing-menu" onClick={() => setMobileNav((open) => !open)} aria-expanded={mobileNav} aria-label="Abrir navegación">{mobileNav ? <X /> : <Menu />}</button>
      <nav className={mobileNav ? 'open' : ''} aria-label="Navegación principal">
        <a href="#como-funciona">Cómo funciona</a>
        <a href="#funciones">Funciones</a>
        <a href="#preguntas">Preguntas</a>
        <a href="/app/" className="header-login">Ingresar</a>
        <a href="/app/?mode=signup" className="primary-button">Crear cuenta gratis <ArrowRight size={17} /></a>
      </nav>
    </header>

    <main id="contenido">
      <section className="marketing-hero">
        <div className="hero-copy">
          <span className="hero-badge"><Sparkles size={15} /> Productividad sin depender de tu memoria</span>
          <h1>Tus pendientes vuelven.<br /><em>Hasta que los hagas.</em></h1>
          <p>TodoBack es una app gratuita de tareas y recordatorios persistentes. Anotá lo que necesitás hacer, elegí cuándo recibir el aviso y volvé a enfocarte en tu día.</p>
          <div className="hero-actions">
            <a href="/app/?mode=signup" className="primary-button hero-primary">Empezar gratis <ArrowRight size={19} /></a>
            {installPrompt && <button className="secondary-button" onClick={() => void install()}><Download size={18} /> Instalar app</button>}
          </div>
          <div className="hero-trust"><span><Check size={15} /> Sin tarjeta</span><span><ShieldCheck size={15} /> Tareas privadas</span><span><Zap size={15} /> Listo en un minuto</span></div>
        </div>
        <div className="product-stage" aria-label="Vista previa de TodoBack">
          <div className="stage-glow" />
          <div className="product-window">
            <div className="product-bar"><Brand compact /><span>Ahora</span><i /><i /><i /></div>
            <div className="product-body">
              <div className="product-focus"><small>PRÓXIMA ACCIÓN</small><b>Enviar propuesta al cliente</b><span><Clock3 size={14} /> Hoy, 16:30</span><button>Empezar ahora <ArrowRight size={14} /></button></div>
              <div className="product-list"><div><span className="fake-check" /><p><b>Revisar estado de la PBI</b><small>Trabajo · Prioridad alta</small></p><BellRing size={17} /></div><div><span className="fake-check" /><p><b>Reservar turno del dentista</b><small>Personal · Mañana</small></p><BellRing size={17} /></div><div><span className="fake-check done"><Check size={12} /></span><p><b>Actualizar el reporte semanal</b><small>Completada hoy</small></p><CheckCircle2 size={17} /></div></div>
            </div>
          </div>
          <div className="floating-reminder"><span><AlarmClock /></span><div><small>TodoBack te recuerda</small><b>Enviar propuesta al cliente</b><p><button>Completar</button><button>En 10 min</button></p></div></div>
        </div>
      </section>

      <section className="problem-strip" aria-label="Beneficios principales">
        <div><b>Capturá en segundos</b><span>Una idea no debería interrumpir tu trabajo.</span></div>
        <div><b>Decidí qué hacer ahora</b><span>Prioridad y contexto, sin listas infinitas.</span></div>
        <div><b>Recibí el aviso a tiempo</b><span>Posponé, repetí o completá desde el recordatorio.</span></div>
      </section>

      <section className="marketing-section centered" id="como-funciona">
        <span className="section-kicker">Un sistema simple</span>
        <h2>De “después lo hago” a realmente hecho</h2>
        <p className="section-lead">TodoBack reduce la organización a tres decisiones fáciles.</p>
        <div className="steps-grid"><article><em>1</em><span><Zap /></span><h3>Anotá el pendiente</h3><p>Guardá una tarea en pocos segundos. Podés agregar el contexto más tarde.</p></article><article><em>2</em><span><CalendarCheck /></span><h3>Elegí cuándo vuelve</h3><p>Definí fecha, prioridad, repetición y cuánto querés que insista.</p></article><article><em>3</em><span><CheckCircle2 /></span><h3>Resolvé o posponé</h3><p>Completá la tarea o elegí un mejor momento sin perderla de vista.</p></article></div>
      </section>

      <section className="marketing-section feature-section" id="funciones">
        <div className="feature-heading"><div><span className="section-kicker">Todo lo importante, sin sobrecarga</span><h2>Una agenda de tareas que trabaja para vos</h2></div><p>Diseñada para seguimientos laborales, trámites, mensajes, rutinas y esos pequeños pendientes que suelen quedar atrás.</p></div>
        <div className="feature-grid">
          <article className="feature-large"><span><BellRing /></span><h3>Recordatorios persistentes</h3><p>Repetí avisos cada pocos minutos, una hora o un día. Posponelos sin reescribir la tarea.</p><div className="mini-notification"><AlarmClock /><p><b>Revisar contrato</b><small>Te lo recordamos porque sigue pendiente.</small></p><button>10 min</button></div></article>
          <article><span><Sparkles /></span><h3>Vista “Ahora”</h3><p>Encontrá la siguiente acción recomendada según prioridad y vencimiento.</p></article>
          <article><span><Repeat2 /></span><h3>Tareas recurrentes</h3><p>Rutinas diarias, semanales, mensuales o anuales sin duplicar trabajo.</p></article>
          <article><span><LockKeyhole /></span><h3>Privacidad por cuenta</h3><p>Autenticación segura y políticas para que solo vos accedas a tus datos.</p></article>
          <article><span><Download /></span><h3>Instalable y sincronizada</h3><p>Usala desde el navegador o instalala como app en dispositivos compatibles.</p></article>
        </div>
      </section>

      <section className="seo-content marketing-section">
        <div><span className="section-kicker">Organizá sin olvidar</span><h2>Recordatorios de tareas para la vida real</h2></div>
        <div><p>Las aplicaciones de listas de tareas funcionan bien cuando recordás abrirlas. TodoBack está pensado para el problema contrario: anotás un pendiente y después tu día continúa. Por eso combina captura rápida, prioridades, tareas vencidas y avisos que vuelven a aparecer.</p><p>Podés usar TodoBack como agenda personal, lista de pendientes del trabajo, organizador diario o aplicación de recordatorios con alarma. Las tareas pueden tener fecha o quedar en una bandeja para revisar después, sin desaparecer.</p></div>
      </section>

      <section className="marketing-section faq-section" id="preguntas">
        <div><span className="section-kicker">Preguntas frecuentes</span><h2>Antes de empezar</h2><p>Lo esencial sobre la app, tus datos y los recordatorios.</p></div>
        <div className="faq-list">{faq.map(([question, answer]) => <details key={question}><summary>{question}<span>+</span></summary><p>{answer}</p></details>)}</div>
      </section>

      <section className="final-cta"><div><span><BellRing /></span><h2>No necesitás acordarte de revisar otra lista.</h2><p>Anotá una vez. TodoBack se ocupa de que el pendiente vuelva.</p><a href="/app/?mode=signup" className="primary-button">Crear mi cuenta gratis <ArrowRight size={18} /></a><small>Gratis · Sin tarjeta · Tus tareas son privadas</small></div></section>
    </main>

    <footer className="marketing-footer"><div><Brand /><p>Tareas y recordatorios que vuelven hasta que los completes.</p></div><nav aria-label="Enlaces legales"><a href="/ayuda/">Ayuda</a><a href="/privacidad/">Privacidad</a><a href="/terminos/">Términos</a><a href="/app/">Ingresar</a></nav><small>© 2026 TodoBack. Hecho para que nada se te escape.</small></footer>
  </div>
}

const legalCopy = {
  privacidad: {
    title: 'Política de privacidad',
    description: 'Cómo TodoBack protege y procesa los datos de tu cuenta, tareas y recordatorios.',
    sections: [
      ['Qué datos procesamos', 'Procesamos el email y la información de acceso necesaria para autenticarte; además de las tareas, etiquetas, preferencias, recordatorios y actividad que decidís guardar. No necesitamos datos sensibles para ofrecer el servicio.'],
      ['Para qué usamos la información', 'Usamos esos datos exclusivamente para prestar, proteger y mejorar TodoBack: sincronizar tu cuenta, ordenar tareas, programar recordatorios y atender solicitudes de soporte. No vendemos datos personales ni usamos el contenido de tus tareas para publicidad.'],
      ['Seguridad y proveedores', 'La autenticación y la base de datos se procesan con Supabase. El alojamiento puede operar sobre Vercel o Netlify. Aplicamos HTTPS, controles de acceso por cuenta y Row Level Security para que cada usuario solo pueda consultar y modificar sus propios registros.'],
      ['Conservación y eliminación', 'Conservamos la información mientras tu cuenta esté activa. Podés eliminarla desde la configuración de TodoBack. La eliminación de cuenta borra los datos asociados según las reglas de cascada de la base de datos.'],
      ['Tus decisiones', 'Podés bloquear notificaciones desde el navegador, desactivar Web Push, corregir tu nombre visible y eliminar tu cuenta. También podés contactarnos para solicitar acceso o aclaraciones sobre tus datos.'],
    ],
  },
  terminos: {
    title: 'Términos de uso',
    description: 'Condiciones para usar gratuitamente TodoBack y sus funciones de tareas y recordatorios.',
    sections: [
      ['Uso del servicio', 'TodoBack ofrece una herramienta personal de productividad sin costo. Al crear una cuenta aceptás usarla de forma lícita, proporcionar información correcta y mantener seguras tus credenciales.'],
      ['Disponibilidad y recordatorios', 'Trabajamos para brindar un servicio confiable, pero no garantizamos disponibilidad ininterrumpida ni entrega exacta en todos los dispositivos. TodoBack no reemplaza sistemas de alarma críticos, médicos, legales, financieros o de emergencia.'],
      ['Tu contenido', 'Conservás la titularidad de tus tareas y descripciones. Nos autorizás únicamente a procesarlas para operar el servicio. No publiques contenido ilícito ni intentes afectar la disponibilidad o seguridad de la plataforma.'],
      ['Cuenta y finalización', 'Podés cerrar tu cuenta desde la configuración. Podemos limitar cuentas que abusen del servicio, comprometan su seguridad o incumplan estas condiciones.'],
      ['Cambios', 'Podemos actualizar estos términos para reflejar mejoras o requisitos legales. Cuando un cambio sea relevante, actualizaremos la fecha de esta página.'],
    ],
  },
} as const

export function LegalPage({ kind }: { kind: keyof typeof legalCopy }) {
  const page = legalCopy[kind]
  usePublicMeta(`${page.title} — TodoBack`, page.description)
  return <div className="simple-public-page"><header><a href="/"><Brand /></a><a href="/app/" className="secondary-button">Volver a la app</a></header><main><span className="section-kicker">TodoBack</span><h1>{page.title}</h1><p className="legal-intro">{page.description}</p>{page.sections.map(([title, copy]) => <section key={title}><h2>{title}</h2><p>{copy}</p></section>)}<p className="legal-date">Última actualización: 12 de julio de 2026.</p></main><footer><a href="/">Inicio</a><a href="/privacidad/">Privacidad</a><a href="/terminos/">Términos</a><a href="/ayuda/">Ayuda</a></footer></div>
}

export function HelpPage() {
  usePublicMeta('Ayuda y preguntas frecuentes — TodoBack', 'Cómo crear tareas, activar recordatorios, instalar TodoBack y resolver problemas de notificaciones.')
  return <div className="simple-public-page"><header><a href="/"><Brand /></a><a href="/app/" className="primary-button">Abrir TodoBack</a></header><main><span className="section-kicker">Centro de ayuda</span><h1>¿Cómo podemos ayudarte?</h1><p className="legal-intro">Respuestas rápidas para configurar TodoBack y recibir tus recordatorios.</p><div className="help-grid">{faq.slice(1).map(([question, answer]) => <article key={question}><h2>{question}</h2><p>{answer}</p></article>)}</div><section><h2>¿Seguís con un problema?</h2><p>Ingresá a TodoBack y usá la opción “Enviar comentarios” en Configuración. Incluí qué navegador y dispositivo estás usando, sin copiar contraseñas ni información sensible.</p></section></main><footer><a href="/">Inicio</a><a href="/privacidad/">Privacidad</a><a href="/terminos/">Términos</a></footer></div>
}

export function NotFoundPage() {
  usePublicMeta('Página no encontrada — TodoBack', 'La página solicitada no existe.', 'noindex,nofollow')
  return <div className="not-found-page"><Brand /><span>404</span><h1>Este pendiente no existe</h1><p>La página que buscás cambió de lugar o nunca estuvo acá.</p><a href="/" className="primary-button">Volver al inicio</a></div>
}
